/**
 * Fallback default value
 */
function defaultTo(valueArg, defaultValue) {
  const value = valueArg;
  return value == null || value !== valueArg ? defaultValue : value;
}

function generateRandomId() {
  return Math.random().toString(36).substring(2, 12);
}

/**
 * Formats an amount in cents into a currency string using a specified or default format.
 *
 * @param {number|string} centsArg - The amount in cents.
 * @param {string} [formatArg] - Optional format string. Defaults to `window.theme.settings.money_format`.
 * @returns {string} Formatted currency string.
 *
 * The function supports formatting with or without decimals, and with different separators.
 * Examples of format placeholders: `amount`, `amount_no_decimals`, `amount_with_comma_separator`.
 */
function formatMoney(centsArg, formatArg) {
  let cents = centsArg;
  let format = formatArg;
  if (!format) {
    format = window.theme.settings.money_format;
  }

  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format;

  function formatWithDelimiters(numberArg, precisionArg, thousandsArg, decimalArg) {
    let number = numberArg;
    let precision = precisionArg;
    let thousands = thousandsArg;
    let decimal = decimalArg;

    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ',');
    decimal = defaultTo(decimal, '.');

    if (Number.isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

/**
 * throttle function
 * @param {string} callback the function
 * @param {number} delay delay between interactions
 */
function throttle(callback, delay) {
  let inProgress = false;

  return (...args) => {
    if (inProgress) return;

    inProgress = true;
    setTimeout(() => {
      callback(...args);
      inProgress = false;
    }, delay);
  };
}

/**
 * debounce function
 * @param {string} callback the function
 * @param {number} delay delay between interaction is done and callback is called
 */
function debounce(callback, delay) {
  let timeout;

  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      callback(...args);
    }, delay);
  };
}

/**
 * debouncePromise function
 * @param {string} callback the function
 * @param {number} delay delay between interaction is done and callback is called
 */
function debouncePromise(callback, delay) {
  let timeout;
  let resolvePromise;

  return (...args) => {
    clearTimeout(timeout);
    return new Promise((resolve) => {
      resolvePromise = resolve;
      timeout = setTimeout(async () => {
        await callback(...args);
        resolvePromise();
      }, delay);
    });
  };
}

function getStockLevel(variant, stockThreshHold) {
  if (variant.quantity <= 0) {
    return {
      text: variant.metafields.custom.out_of_stock_text,
      color: variant.stock_level_colors.out_of_stock_color,
    };
  }

  if (variant.quantity >= 1 && variant.quantity <= stockThreshHold) {
    return {
      text: variant.metafields.custom.low_stock_text,
      color: variant.stock_level_colors.low_stock_color,
    };
  }

  return {
    text: variant.metafields.custom.in_stock_text,
    color: variant.stock_level_colors.in_stock_color,
  };
}

function calculatePriceDiscount(price, compareAtPrice) {
  if (!price || !compareAtPrice) {
    return '';
  }
  return Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
}

function convertMMtoFeetAndInches(valueInMm) {
  const INCHES_TO_MM = window.constants?.INCHES_TO_MM ?? 25.4;
  const INCHES_PER_FOOT = window.constants?.INCHES_PER_FOOT ?? 12;

  const totalInches = Math.round(valueInMm / INCHES_TO_MM);
  const feet = Math.floor(totalInches / INCHES_PER_FOOT);
  const inches = totalInches % INCHES_PER_FOOT;

  return `${feet}′ ${inches}″`;
}

function convertFeetAndInchesToMM(feetInchesString) {
  const FEET_TO_MM = window.constants?.FEET_TO_MM ?? 304.8;
  const INCHES_TO_MM = window.constants?.INCHES_TO_MM ?? 25.4;

  if (!feetInchesString || typeof feetInchesString !== 'string') {
    return NaN;
  }

  const cleanInput = feetInchesString.trim();

  // Parse only proper foot/inch format like "12′ 6″" or "12' 6\"" to avoid ambiguity
  const match = cleanInput.match(/(\d+)[′']\s*(\d+)[″"]/);

  if (!match) return NaN;

  const feet = parseInt(match[1], 10);
  const inches = parseInt(match[2], 10);

  if (Number.isNaN(feet) || Number.isNaN(inches) || feet < 0 || inches < 0 || inches >= 12) {
    return NaN;
  }

  return Math.round(feet * FEET_TO_MM + inches * INCHES_TO_MM);
}

export {
  generateRandomId,
  formatMoney,
  throttle,
  debounce,
  debouncePromise,
  getStockLevel,
  calculatePriceDiscount,
  convertMMtoFeetAndInches,
  convertFeetAndInchesToMM,
};
